/** @author PVCOMBANK */
import React, { useEffect, useState, useMemo } from "react";
import { Paragraph, TextInput, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { InputSegment, InputText, InputAutoComplete, InputTextCurrency, InputTextMultiline } from "app/shared/components/form/input";
import { StyleSheet, View } from "react-native";
import { amountConfig, getBank247, getListBank247, loadBank247 } from "app/modules/transfer/transfer-utils/transfer.utils";
import { Constant } from "app/shared/constant/constant";
import { PROFILE_BENE } from "app/modules/beneficiaries/utils/bene.utils";
import { ACCOUNT, EBINQUIRY } from "app/shared/services/services-model.type";
import { useDispatch, useSelector } from "react-redux";
import { nonAccentVietnamese, regexCharacterMarkSpaceDot, replaceEnterKey, replaceSpace } from "app/shared/util/validate";
import { fetchTransfer247AccountDetailRequest, setTransfer247Store } from "app/modules/transfer/transfer-247/reducer/actions";
import {
  getTransfer247AccountCreditSelector,
  getTransfer247MakeFeeSelector,
  getTransfer247StoreSelector,
} from "app/modules/transfer/transfer-247/reducer/selectors";
import { DataTransferCreate } from "app/modules/transfer/reducer/types";
import { FeeDetailModalNew } from "app/shared/components/modal/make-fee/fee-detail.modal";
import { getTransferInfoPendingSelector } from "app/modules/transfer/reducer/selectors";
import BeneModal from "app/shared/components/modal/bene-account-source/select-bene.modal";
import IconsYellowBanking from "app/shared/components/icons/icon-yellow-banking/IconsYellowBanking";
import FormViewFee from "app/shared/components/form/fee/form-fee";
import { ConvertCurrencyToVietnamese } from "app/shared/util/format-currency";
import { resetListBene } from "app/modules/beneficiaries/reducer/actions";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import { getLimitRemainSelector } from "app/modules/account-management/reducer/selectors";

export interface FormTrans247Props {
  onSubmit: Function;
  accountDebit: ACCOUNT;
  onCancel: Function;
  requestMakeFee: Function;
  requestCreate: Function;
  showModal: Function;
  closeModal: Function;
}

enum FormEnum {
  accountNo,
  accountName,
  bankCode,
  bankName,
  bankBinCode,
  amount,
  description,
  feeType,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export interface Trans247Data {
  accountNo: string;
  accountName: string;
  bankCode: string;
  bankName: string;
  bankBinCode: string;
  amount: string;
  description: string;
  feeType: string;
  accountType: string;
}

const FormTransfer247 = (props: FormTrans247Props) => {
  const { requestCreate, requestMakeFee, onCancel, accountDebit, showModal, closeModal } = props;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useDesktopMediaQuery();
  const convertCurrencyToVietnamese = new ConvertCurrencyToVietnamese();
  const transfer247Store = useSelector(getTransfer247StoreSelector);
  const limitRemainStore = useSelector(getLimitRemainSelector);
  const responseMakeFee = useSelector(getTransfer247MakeFeeSelector);
  const accountCredit = useSelector(getTransfer247AccountCreditSelector);
  const pendingInfo = useSelector(getTransferInfoPendingSelector);

  const [validateAmount, setValidateAmount] = useState({ maxAmount: 0, errorMaxDebit: "" });
  // Change name label when select account type;
  const [bankArray, setBankArray] = useState<ItemCompleteProps[]>([]);
  const [dataFee, setDataFee] = useState<EBINQUIRY | undefined>(undefined);
  const [accountGetDetail, setAccountGetDetail] = useState<ACCOUNT | undefined>();
  const [isBene, setIsBene] = useState<boolean>(false);
  const [accountBene, setAccountBene] = useState<PROFILE_BENE | undefined>();
  const [isSelectBank, setIsSelectBank] = React.useState<boolean>(false);

  // Instance Form Method
  const { control, handleSubmit, setValue, getValues, clearErrors, trigger, watch } = useForm<Trans247Data>({
    defaultValues: {
      accountNo: "",
      accountName: "",
      bankCode: "",
      bankName: "",
      bankBinCode: "",
      amount: "",
      description: "",
      feeType: "0",
      accountType: "0",
    },
    mode: "onChange",
  });

  const watchAccountType = watch("accountType", "0");

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    accountNo: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_TAI_KHOAN"),
      },
    },
    accountName: {
      required: {
        value: false,
        message: "",
      },
    },
    amount: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_SO_TIEN"),
      },
      validate: {
        amountMin: (value) => {
          if (Number(value) < 10000) {
            return t("COMMON_SO_TIEN_KHONG_DUOC_DUOI_10000D").toString();
          }
          return true;
        },
        amountMax: (value) => {
          if (validateAmount?.maxAmount && Number(value) > validateAmount?.maxAmount) {
            return validateAmount?.errorMaxDebit;
          }
          return true;
        },
        amountMaxOne: (value) => {
          if (Number(value) > 500000000) {
            const amount = convertCurrencyToVietnamese.convert(500000000);
            return t("TRANFER_SO_TIEN_KHONG_DUOC_VUOT_TREN_LAN").replace("{amount}", amount);
          }
          return true;
        },
      },
    },
    bankCode: {
      required: {
        value: true,
        message: t("TRANFER_VUI_LONG_CHON_NGAN_HANG"),
      },
    },
    bankName: {
      required: {
        value: false,
        message: "",
      },
    },
    bankBinCode: {
      required: {
        value: false,
        message: "",
      },
    },
    description: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_CHUA_NHAP_NOI_DUNG"),
      },
      validate: {
        regexChar: (value) => {
          const regex = regexCharacterMarkSpaceDot();
          if (!regex.test(value)) {
            return t("TRANFER_NOI_DUNG_CHUYEN_TIEN_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
    feeType: {
      required: {
        value: true,
        message: "",
      },
    },
  };

  const handleRequestAccountCreditDetail = async () => {
    const accountNo = getValues("accountNo");
    const bankCode = getValues("bankCode");
    const bankName = getValues("bankName");
    const bankBinCode = getValues("bankBinCode");
    if (!accountNo.length && accountCredit?.ACCOUNTNO?.length) {
      dispatch(setTransfer247Store({ ...transfer247Store, accountCredit: undefined }));
    }
    if (accountNo.length && bankCode.length) {
      const dataUpdate: DataTransferCreate = {
        ACCOUNT_DEBIT: accountDebit,
        ACCOUNT_CREDIT: {
          ACCOUNTNO: accountNo,
          ACCOUNTTYPE: "10",
        },
        BANK: { value: bankCode, label: bankName, other: bankBinCode },
        AMOUNT: "10000",
        DESCRIPTION: "ACCOUNT_DETAIL",
        FEE_TYPE: "0",
        TYPE: getValues("accountType") === "0" ? Constant.TRANSFER.TRANSFER_247_TYPE.ACC : Constant.TRANSFER.TRANSFER_247_TYPE.CRD,
      };
      setAccountGetDetail({ ...dataUpdate.ACCOUNT_CREDIT, BANK_CODE: bankCode });
      dispatch(fetchTransfer247AccountDetailRequest(dataUpdate));
    }
  };

  // Lấy danh sách bank
  const getListBankData = React.useCallback(() => {
    if (!bankArray.length) {
      const listBank = getListBank247();
      setBankArray(listBank);
    }
  }, [bankArray]);

  React.useEffect(() => {
    if (accountCredit === undefined && getValues("bankCode").length > 0) {
      setValue("accountNo", "");
    }
  }, [accountCredit, getValues, setValue]);

  const handleRequestCreate = (value: Trans247Data): void => {
    const dataUpdate: DataTransferCreate = {
      ACCOUNT_CREDIT: {
        ACCOUNTNO: value.accountNo,
        ACCOUNTTYPE: "10",
        ACCOUNTNAME: value.accountName,
      },
      BANK: { value: value.bankCode, label: value.bankName, other: value.bankBinCode },
      AMOUNT: value.amount,
      DESCRIPTION: value.description,
      FEE_TYPE: value.feeType === "0" ? "O" : "I",
      TYPE: value.accountType === "0" ? Constant.TRANSFER.TRANSFER_247_TYPE.ACC : Constant.TRANSFER.TRANSFER_247_TYPE.CRD,
      BENE: isBene && accountBene?.ACCOUNT_NUMBER?.length && accountBene?.ACCOUNT_NUMBER === value.accountNo ? accountBene : undefined,
    };
    requestCreate(dataUpdate);
  };

  const handleRequestMakeFee = (value: Trans247Data): void => {
    const dataUpdate: DataTransferCreate = {
      ACCOUNT_CREDIT: {
        ACCOUNTNO: value.accountNo,
        ACCOUNTTYPE: "10",
        ACCOUNTNAME: value.accountName,
      },
      BANK: { value: value.bankCode, label: value.bankName, other: value.bankBinCode },
      AMOUNT: value.amount,
      DESCRIPTION: value.description,
      FEE_TYPE: value.feeType === "0" ? "O" : "I",
      TYPE: value.accountType === "0" ? Constant.TRANSFER.TRANSFER_247_TYPE.ACC : Constant.TRANSFER.TRANSFER_247_TYPE.CRD,
    };
    if (dataFee) setDataFee(undefined);
    requestMakeFee(dataUpdate);
  };

  const handlerSelectBene = (account: PROFILE_BENE) => {
    closeModal();
    let accountNo = account?.ACCOUNT_NUMBER || "";
    if (account.CARD_NUMBER?.length) {
      accountNo = account.CARD_NUMBER || "";
      setValue("accountType", "1");
    } else if (account?.ACCOUNT_NUMBER?.length) {
      setValue("accountType", "0");
    }
    setValue("accountNo", accountNo);
    setValue("bankCode", account.BANK_NAME || "");
    const bank = getBank247(account.BANK_NAME);
    const bankName = bank.label || "";
    const bankBinCode = bank.other || "";
    setValue("bankName", bankName);
    setValue("bankBinCode", bankBinCode);
    setIsBene(true);
    setAccountBene(account);
    handleRequestAccountCreditDetail();
  };

  const onChangeBank = async (bank?: ItemCompleteProps) => {
    setValue("bankName", bank?.label || "");
    setValue("bankBinCode", bank?.other || "");
    if (getValues("accountNo").length) {
      setValue("accountNo", "");
      setValue("accountName", "");
    }
  };

  const onPressFeeDetail = async () => {
    handleSubmit(handleRequestMakeFee).call(() => {});
  };

  const onPressBeneAndAccount = () => {
    dispatch(resetListBene());
    showModal(<BeneModal templateType={Constant.TEMPLATE_TYPE.INTERBANK_FAST_TRANSFER} onPressAcceptBene={handlerSelectBene} />);
  };

  const onPressCreate = () => {
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onChangeAccountText = () => {
    if (accountGetDetail) setAccountGetDetail(undefined);
  };

  // set thông tin tk nhận
  const onBlurAccountCredit = async () => {
    const bankTrigger = await trigger("bankCode");
    const accountNo = getValues("accountNo");
    const bankCode = getValues("bankCode");
    if (
      bankTrigger &&
      accountBene?.BANK_NAME === bankCode &&
      accountNo.length &&
      accountBene?.ACCOUNT_NUMBER?.length &&
      accountNo === accountBene?.ACCOUNT_NUMBER
    ) {
      setIsBene(true);
    } else {
      setIsBene(false);
    }
    if (bankTrigger && accountNo.length && (accountNo !== accountGetDetail?.ACCOUNTNO || bankCode !== accountGetDetail.BANK_CODE))
      handleRequestAccountCreditDetail();
  };

  // fix/replace nội dung chuyển tiền
  const onBlurDescription = () => {
    const value = getValues("description") || "";
    setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(value.trim()))));
    trigger("description");
  };

  // Styles Component
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { flex: 1 },
        fieldText: { color: colors.text, fontWeight: "700", fontSize: 16 },
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
        inputDisplay: { display: accountCredit?.ACCOUNTNAME?.length ? "flex" : "none" },
        displayAccountNo: { display: watchAccountType === "0" ? "flex" : "none" },
        displayATM: { display: watchAccountType === "1" ? "flex" : "none" },
      }),
    [colors.text, colors.notification, accountCredit, watchAccountType],
  );

  const chooseAccountBene = (
    <TextInput.Icon name={() => <IconsYellowBanking name={"danhsachthuhuong"} size={28} />} size={28} onPress={onPressBeneAndAccount} />
  );

  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormAccountType = (
    <InputSegment
      label={t("TRANFER_247_KIEU")}
      name="accountType"
      control={control}
      values={[t("TRANFER_SO_TAI_KHOAN"), t("TRANFER_SO_THE_ATM")]}
    />
  );

  const FormFee = (
    <>
      <InputSegment
        label={t("TRANFER_BEN_CHIU_PHI")}
        name="feeType"
        control={control}
        values={[t("TRANFER_BEN_CHUYEN"), t("TRANFER_BEN_NHAN")]}
      />
      <FormViewFee onClick={onPressFeeDetail} />
    </>
  );

  const FormsInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      {FormAccountType}
      <StackSpacing size="small" />
      <InputAutoComplete
        title={t("TRANFER_CHON_NGAN_HANG")}
        options={bankArray}
        name="bankCode"
        rules={validateSchemeForm.bankCode}
        pending={pendingInfo}
        control={control}
        label={t("TRANFER_TAI_NGAN_HANG")}
        checkSelect={!isSelectBank}
        onDismiss={onChangeBank}
        showSearch={true}
        typeSearch={Constant.TEMPLATE_TYPE.INTERBANK_FAST_TRANSFER}
        showIcon={true}
      />

      <View style={styles.displayAccountNo}>
        <InputText
          rules={validateSchemeForm.accountNo}
          name="accountNo"
          control={control}
          inputProps={{ right: chooseAccountBene }}
          label={t("TRANFER_SO_TAI_KHOAN")}
          onChangedText={onChangeAccountText}
          onBlur={onBlurAccountCredit}
        />
      </View>

      <View style={styles.displayATM}>
        <InputText
          rules={validateSchemeForm.accountNo}
          name="accountNo"
          control={control}
          inputProps={{ right: chooseAccountBene }}
          label={t("TRANFER_SO_THE_ATM")}
          onChangedText={onChangeAccountText}
          onBlur={onBlurAccountCredit}
        />
      </View>
      <InputText
        rules={validateSchemeForm.accountName}
        name="accountName"
        control={control}
        inputProps={{ editable: false }}
        style={styles.inputDisplay}
        label={t("TRANFER_TEN_TAI_KHOAN")}
      />
      <InputTextCurrency
        name="amount"
        keyboardType={"numeric"}
        control={control}
        onBlur={onBlurDescription}
        rules={validateSchemeForm.amount}
        label={t("TRANFER_SO_TIEN")}
        rightCurrency={t("COMMON_VND")}
      />
      <InputTextMultiline
        name="description"
        control={control}
        rules={validateSchemeForm.description}
        label={t("TRANFER_NOI_DUNG_CHUYEN_TIEN")}
        note={t("COMMON_NOTE_DESCRIPTION")}
        onBlur={onBlurDescription}
        inputProps={{ multiline: true, maxLength: 100 }}
        rightAffix={true}
      />
      {FormFee}
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={() => onCancel()} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  // map nội dung mặc định
  useEffect(() => {
    if (accountDebit.ACCOUNTNAME?.length && !getValues("description").length) {
      const content = accountDebit.ACCOUNTNAME.toUpperCase() + " " + t("TRANFER_DESCRIPTION_CHUYEN_TIEN").toLowerCase();
      setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(content))));
      clearErrors("description");
    }
  }, [accountDebit, clearErrors, getValues, setValue, t]);

  // account credit detail
  useEffect(() => {
    console.log("accountCredit: ", accountCredit);
    setValue("accountNo", accountCredit?.ACCOUNTNO || "");
    setValue("accountName", accountCredit?.ACCOUNTNAME || "");
  }, [accountCredit, setValue]);

  useEffect(() => {
    // lấy hạn mức gd ngày/ số dư khả dụng
    const { maxAmount, errorMaxDebit } = amountConfig(accountDebit, limitRemainStore, getValues("accountNo"));
    setValidateAmount({ maxAmount, errorMaxDebit });
  }, [limitRemainStore, accountDebit, getValues]);

  useEffect(() => {
    loadBank247().then(() => {
      getListBankData();
    });
  }, [getListBankData]);

  useEffect(() => {
    if (bankArray.length && !isSelectBank) setIsSelectBank(true);
  }, [bankArray, isSelectBank]);

  // thêm thụ hưởng thành công
  useEffect(() => {
    if (validateAmount?.errorMaxDebit?.length && getValues("amount").length) trigger("amount");
  }, [getValues, trigger, validateAmount]);

  useEffect(() => {
    if (!accountBene?.ACCOUNT_NUMBER?.length && transfer247Store.dataCreate?.BENE?.ACCOUNT_NUMBER) {
      setAccountBene(transfer247Store.dataCreate?.BENE);
      if (transfer247Store.dataCreate?.BENE?.ACCOUNT_NUMBER === getValues("accountNo")) setIsBene(true);
    }
  }, [accountBene, getValues, transfer247Store]);

  useEffect(() => {
    if (responseMakeFee?.TRANSFER) {
      setDataFee(responseMakeFee.TRANSFER);
    }
  }, [responseMakeFee]);

  // // show Phí
  useEffect(() => {
    if (dataFee) {
      showModal(
        <FeeDetailModalNew
          data={dataFee}
          feeType={getValues("feeType") === "0" ? Constant.TRANSFER.TRANSFER_FEE_TYPE.O : Constant.TRANSFER.TRANSFER_FEE_TYPE.I}
        />,
      );
      setDataFee(undefined);
    }
  }, [accountDebit, getValues, dataFee, showModal]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormsInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormTransfer247;
